import { crudGetList, HttpError, ListContextProvider, ReduxState, ResourceContextProvider, useListContext, useListController, useResourceContext } from "ra-core"
import { FC, useEffect, useRef, useState } from 'react';
import LoadingButton from "@mui/lab/LoadingButton";
import DownloadIcon from "@mui/icons-material/Download"
import { Box, Card, Divider, Typography, Link, Tooltip, Stack, Popover, FormGroup, FormControlLabel, Checkbox, FormHelperText, Button } from "@mui/material";
import { Link as RouteLink } from "react-router-dom";
import { OdResourcePageProps } from "@/types/resource";
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { Content } from "@/components/content/Content"
import { Datagrid } from "@/components/list/datagrid";
import Scrollbar from "@/components/Scrollbar";
import { FileCataSearchContextProvider, FileCataSearchStates, osfCatalogRes, useFileCataSearchStates } from "@/components/securefiles/FileCataSearchContextProvider";
import { FileCataAmbientDataContextProvider, useFileCataAmbientDataContext } from "@/components/securefiles/FileCataAmbientDataProvider";
import FileCatalogSearchRegion from "@/components/securefiles/FileCatalogSearchRegion";
import RowExpandFileCataEdit from "@/components/securefiles/RowExpandFileCataEdit";
import { FileResponseDto } from "@/gen/api/securefile";
import { RecPatch } from "@/dataProvider/tools";
import { FunctionField, TextField } from "@/components/field";
import ClassificationLabel from "@/components/governance/labels/ClassificationLabel";
import { genUrlWithHashid } from "@/utils/hashid";
import { usePushBreadcrumb } from "@/utils/usePushBreadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { useResourceRegister } from "@/utils/resourceRegister";
import { useSnackbar } from "notistack";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_DATA_CATALOG } from "@/constants";
import FileCataClassificationStatistics from "@/components/securefiles/FileCataClassificationStatistics";
import FileCataOwnershipPieChart from "@/components/securefiles/FileCataOwnershipPieChart";
import { crudGetManyEx } from "@/actions/crudFetchEx";


const pageMark = { label: 'File Catalog' }
const exportRes = 'secure-files-catalog-export'
const exportResWithInstance = 'secure-files-catalog-export-with-instance'

const FileCatalog: FC<OdResourcePageProps> = (props) => {
  useResourceRegister({ name: exportRes })
  useResourceRegister({ name: exportResWithInstance })
  useResourceRegister({ name: 'secure-files/file-catalog-statis' })
  useResourceRegister({ name: 'secure-files/pfs-statis' })
  useResourceRegister({ name: 'secure-files-instances-count' })

  const { location } = props

  const { states, setStates, delayedInitialListQuery } = useFileCataSearchStates((location?.state ?? {} as any).currRes)
  const { current: filterDefaultValues } = useRef(getFilterDefaultValues(states.filters))
  const listCtrl = useListController({ resource: states.currRes, basePath: '', filterDefaultValues })
  usePushBreadcrumb((to: {pathname:string}) => {
    console.log(to)
    return {...pageMark, path: location!.pathname} as any
  }, { toMatch: '/secure-files/'})

  const { error } = listCtrl
  const { enqueueSnackbar } = useSnackbar()
  useEffect(() => {
    if (error) {
      enqueueSnackbar(`Fetch data failed: ${(error as HttpError).message}`, { variant: 'error' })
    }
  }, [enqueueSnackbar, error])
  
  return (
    <ResourceContextProvider value={states.currRes}>
      <ListContextProvider value={listCtrl}>
        <FileCataSearchContextProvider {...{ states, setStates, delayedInitialListQuery }}>
          <FileCataAmbientDataContextProvider ownerGroup={true}>
            <FileCatalogBody {...props} states={states} />
          </FileCataAmbientDataContextProvider>
        </FileCataSearchContextProvider>
      </ListContextProvider>
    </ResourceContextProvider>
  )
}

const FileCatalogBody: FC<OdResourcePageProps & { states: UIState }> = ({
  options,
  states
}) => {
  const hasDCWritePermit = useHasPermit([MANAGE_DATA_CATALOG])
  
  const listCtx = useListContext()
  const { classifications, tags } = useFileCataAmbientDataContext()
  usePrepareDataOwnerGroupData()

  return (
    <Content pageName="File Catalog" resourceName="File Catalog" breadcrumbs={options?.breadcrumbs} >
      <Stack direction="row" alignItems="baseline">
        <FileCataClassificationStatistics />
        {hasDCWritePermit && <ExportFileCatalogButton />}
      </Stack>
      <Box sx={{ mt: -2, mb: 4 }}>
        <FileCataOwnershipPieChart />
      </Box>
      <Card>
        <FileCatalogSearchRegion />
        <Divider />
        <Scrollbar options={{ suppressScrollY: true }}>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid
              hasBulkActions={false}
              expand={<RowExpandFileCataEdit />}
              sx={{
                '& th' : { py: 1, textTransform: 'uppercase' },
                '& td' : { py: 2 },
                '& span.MuiTypography-root.filename' : { maxWidth:400, wordBreak:'break-word' },
              }}
            >
              <FunctionField source="fileName" label={<Box component="span" sx={{ maxWidth: 100 }}>Name</Box>} sortable={false} 
                render={(d?:FileResponseDto&RecPatch) =>
                  <Typography component="span" variant="body2" className="filename">
                    <Link underline="none"
                      component={RouteLink}
                      to={genUrlWithHashid(
                        states.currRes === osfCatalogRes ? '/secure-files/$0/show' : '/secure-files/pfs/$0/show', 
                        d?.fileId ?? (d as any).entryId ?? 0, d?.fileName
                      )}
                    >
                      {d?.fileName}
                    </Link>
                  </Typography>
                }
              />
              <FunctionField source="secondaryClassificationId" label="Classification" sortable={false} 
                render={(d?:FileResponseDto&RecPatch) => {
                  const clss = classifications.find(c => c.secondaryClassificationId === d?.secondaryClassificationId)
                  if (!clss) { return null }
                  return <ClassificationLabel variant={clss.primaryClassification!} text={clss.name!} />;
                }}
              />
              {states.currRes === osfCatalogRes &&
                <FunctionField source="parentFileName" label="Parent File" sortable={false} 
                  render={(d?:FileResponseDto&RecPatch) => 
                    Boolean(d?.parentFileId) &&
                    <Typography component="span" variant="body2" className="filename">
                      <Link underline="none" color={d?.parentFileDeleted ? 'error' : undefined}
                        component={RouteLink}
                        to={genUrlWithHashid(
                          states.currRes === osfCatalogRes ? '/secure-files/$0/show' : '/secure-files/pfs/$0/show', 
                          d?.parentFileId ?? 0, d?.parentFileName
                        )}
                      >
                        {d?.parentFileName}
                      </Link>
                    </Typography>
                  }
                />}
              <FunctionField source="tags" label="Tags" sortable={false} 
                render={(d?:FileResponseDto&RecPatch) => {
                  const filetags = (d?.tags ?? []).map(tid => tags.find(t => t.tagId === tid)).filter(x => !!x)
                  return (
                    <Box sx={{
                      maxWidth: 180,
                      '& p': { fontStyle: 'italic' }
                    }}>
                      {filetags.map(t =>
                        <Typography key={t?.tagId} variant="caption" noWrap component="p">{t?.name}</Typography>
                      )}
                    </Box>
                  )
                }}
              />
              {states.currRes === osfCatalogRes &&
                <TextField source="encryptedByUser" label="Encrypted By" sortable={false} />}
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore
          <DefaultPagination {...listCtx} />}
      </Card>

    </Content>
  )
}


export default FileCatalog;

interface UIState {
  currRes: string;
}


const ExportFileCatalogButton: FC = () => {
  const currRes = useResourceContext()
  const { filterValues } = useListContext()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const [states, setStates] = useState({
    count: 0,
    loading: false,
    anchorEl1: null as HTMLButtonElement|null,
    confirmOptions: {
      withInstance: false
    }
  })
  const startExport = () => {
    setStates(s => ({...s, loading: true }))
    if (states.confirmOptions.withInstance) {
      dispatch(crudGetList(exportResWithInstance, pages(), sorts(), {...filterValues, _count: states.count, pageIndex: 1, pageSize: 100, res: currRes }))
    } else {
      dispatch(crudGetList(exportRes, pages(), sorts(), {...filterValues, _count: states.count, pageIndex: 1, pageSize: 100, res: currRes }))
    }
  }

  const handleClose = () => { setStates(v => ({...v, anchorEl1: null}))}

  useSelector<ReduxState, any>(store => {
    const data = store.admin.resources[exportRes]?.data[-1]
    if (states.loading && states.count === data?.count)
    {
      if (data?.errMsg) {
        enqueueSnackbar(`Failed to download export: ${data.errMsg}`, { variant: 'error' })
      }
      setStates(s => ({ ...s, loading: false, count: s.count + 1 }))
    }
    const dataWithInstance = store.admin.resources[exportResWithInstance]?.data[-1]
    if (states.loading && states.count === dataWithInstance?.count)
    {
      if (dataWithInstance?.errMsg) {
        enqueueSnackbar(`Failed to download export: ${dataWithInstance.errMsg}`, { variant: 'error' })
      }
      setStates(s => ({ ...s, loading: false, count: s.count + 1 }))
    }
  })
  return (
    <Stack sx={{ ml: 'auto!important', mr: theme => `${theme.spacing(2)}!important` }}>
      <Tooltip title="Please adjust search criteria so that exported csv file contains less than 100,000 rows.">
      <LoadingButton
        color="primary"
        startIcon={<DownloadIcon fontSize="small" />}
        sx={{ ml: 'auto!important', mr: theme => `${theme.spacing(2)}!important` }}
        variant="text"
        loading={states.loading}
        loadingPosition="start"
        onClick={(e) => setStates(v => ({...v, anchorEl1:e.currentTarget}))}
      >
        Export
      </LoadingButton>
    </Tooltip>
      <Popover id={'pop-export'} anchorOrigin={{ vertical: 'bottom', horizontal: 'right', }}  transformOrigin={{ vertical: 'top', horizontal: 'right'}}
          open={!!states.anchorEl1}
          anchorEl={states.anchorEl1}
          onClose={handleClose}
        >
          <Box m={2} width={300}>
            <FormGroup sx={{ mb: 2 }}>
              <FormControlLabel label="Include File Instances" 
                control={<Checkbox 
                  checked={states.confirmOptions.withInstance} 
                  onChange={(e) => setStates(v => ({...v, confirmOptions: {...v.confirmOptions, 
                    withInstance: e.target.checked,
                    ...(!e.target.checked && { overwriteManualClassification: false })
                  }}))}
                  />} 
              />
              <FormHelperText sx={{mt:-0.5}}>Export the file catalog with specific instances for each file, including computer and full path.</FormHelperText>
            </FormGroup>
            <Button variant="contained" color="primary" fullWidth 
              onClick={() => {
                handleClose()
                startExport()
              }}
            >
              Export
            </Button>
          </Box>
        </Popover>
    </Stack>
  )
}


const getFilterDefaultValues = (s0: FileCataSearchStates['filters']) => ({
  term: !s0.name ? null : s0.name.trim(),
  tags: s0.tags.map(x => x.tagId!),
  primaryClassifications: [...s0.primaryClassification] as any,
  secondaryClassifications: s0.secondClassification.map(x => x.secondaryClassificationId!),
  documentTypes: [...s0.documentTypes],
  infoTypes: [...s0.infoTypes],
  fileInstanceOptions: s0.fileInstanceOptions,
  dataOwnerGroupIds: (s0.ownerGroup ?? []).map(o => o.ownerGroupId!),
})


function usePrepareDataOwnerGroupData () {
  const { resource, ids, loading } = useListContext()
  const resGetMny = resource === 'secure-files/file-catalog'
    ? 'secure-files/file/data-owner-group' : 'secure-files/plainfile/data-owner-group'
  const dispatch = useDispatch()

  useEffect(() => {
    if (ids && ids.length > 0 && !loading) {
      dispatch(crudGetManyEx(resGetMny, ids))
    }
  }, [dispatch, ids, loading, resGetMny])
}

